'use client';
import React from 'react';
import { Grid, GridItem, useBreakpointValue } from '@chakra-ui/react';
import RecentlyViewedListSkeleton from './RecentlyViewedListSkeleton';
import { useProductService } from '@/hooks/product/useProductService';
import { ProductStockStatus } from '@/constants/product/product';
import ProductCard from '@/components/products/ProductCard';
import { getLowestPriceProductBasicInfo } from '@/utils/domain/product';
import { useSafeRouter } from '@/hooks/route/useSafeRouter';

type RecentlyViewedListProps = { recentlyViewedIds: string[] };

const RecentlyViewedList = ({ recentlyViewedIds }: RecentlyViewedListProps) => {
  const { push } = useSafeRouter();
  const productLimit = useBreakpointValue({ base: 4, desktop: 5 });
  const { useGetProductsByIds } = useProductService();

  const { data: recentlyViewedData } = useGetProductsByIds({
    ids: recentlyViewedIds.join(','),
  });

  const filteredRecentlyViewedProducts = recentlyViewedData?.filter(
    (product) => product.status === ProductStockStatus.instock,
  );

  return (
    <Grid
      templateColumns={{
        base: 'repeat(2, 1fr)',
        desktop: 'repeat(5, 1fr)',
      }}
      tablet={{
        '& > *:nth-of-type(n+5)': {
          display: 'none',
        },
      }}
      desktop={{
        '& > *:nth-of-type(n+5)': {
          display: 'initial',
        },
      }}
      gap={3}
      width="100%"
    >
      {filteredRecentlyViewedProducts.slice(0, productLimit).map((product) => {
        const productInfo = getLowestPriceProductBasicInfo(product);
        return (
          <GridItem key={productInfo.productId}>
            <ProductCard
              onClick={() => push(productInfo.productLink)}
              name={productInfo.name}
              brand={productInfo.brand}
              productImage={productInfo.productImageUrls[0]}
              sku={productInfo.sku}
              currency={productInfo.currency}
              originalPrice={productInfo.originalPrice}
              salePrice={productInfo.salePrice}
            />
          </GridItem>
        );
      })}
    </Grid>
  );
};

const RecentlyViewedListWithSkeleton = RecentlyViewedList as typeof RecentlyViewedList & {
  Skeleton: typeof RecentlyViewedListSkeleton;
};

RecentlyViewedListWithSkeleton.Skeleton = RecentlyViewedListSkeleton;

export default RecentlyViewedListWithSkeleton;
